import { useEffect, useState } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { AlertTriangle, Trash2, RefreshCw } from "lucide-react";

/**
 * Danger-zone card that lets an admin wipe every seeded demo footprint
 * (sample client + demo books + all their child docs) so a production
 * install starts from a clean slate. Two-step confirmation:
 *   1. Preview shows counts of what will be deleted
 *   2. Admin types "DELETE" to arm the button
 */
const ResetDemoDataCard = ({ lang = "en" }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reset-demo-data/preview");
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadPreview(); }, []);

  const run = async () => {
    if (confirm !== "DELETE") return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-demo-data", { confirm: "DELETE" });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(
        lang === "es"
          ? `Restablecido · ${total} documentos eliminados`
          : `Reset complete · ${total} documents deleted`,
      );
      setConfirm("");
      loadPreview();
      // let admin dashboards refresh
      setTimeout(() => window.dispatchEvent(new Event("rn3:refresh")), 300);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (!preview?.found) {
    return (
      <div className="border border-[#E5E5E5] bg-white px-4 py-3 mt-8" data-testid="reset-demo-empty">
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">
          {lang === "es" ? "Datos de demo" : "Demo data"}
        </div>
        <div className="text-xs text-[#404040] mt-1">
          {lang === "es"
            ? "No hay datos de demo residuales. El sistema ya está limpio."
            : "No demo data lingering. System is production-clean."}
        </div>
      </div>
    );
  }

  const counts = preview.counts || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const armed = confirm === "DELETE";

  return (
    <div className="border border-[#8B1C22] bg-[#FEF7ED] px-4 py-4 mt-8" data-testid="reset-demo-card">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#8B1C22] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="rn3-eyebrow text-[#8B1C22]">
            {lang === "es" ? "Zona peligrosa · Restablecer datos de demo" : "Danger zone · Reset demo data"}
          </div>
          <p className="text-xs text-[#404040] mt-1 leading-relaxed">
            {lang === "es"
              ? "Elimina permanentemente la cuenta de cliente de muestra, los libros de demo y todos los documentos vinculados (transacciones, facturas, reglas, extractos bancarios, etc.). Úsalo antes de lanzar en producción."
              : "Permanently removes the sample client account, demo books, and every linked doc (transactions, invoices, rules, bank statements, etc.). Use once before launching in production."}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] mt-3 mb-3">
            {Object.entries(counts).filter(([, v]) => v > 0).map(([coll, v]) => (
              <div key={coll} className="bg-white border border-[#E5E5E5] px-2 py-1" data-testid={`reset-count-${coll}`}>
                <div className="text-[9px] uppercase tracking-widest text-[#737373]">{coll.replace(/_/g, " ")}</div>
                <div className="font-medium tabular-nums">{v}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-[#8B1C22]">
              {lang === "es"
                ? `Escribe "DELETE" para armar la acción (${total} documentos)`
                : `Type "DELETE" to arm (${total} documents)`}
            </span>
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="DELETE"
              className="border border-[#E5E5E5] px-2 py-1 text-xs uppercase tracking-widest w-28"
              data-testid="reset-demo-confirm-input"
            />
            <button
              onClick={run}
              disabled={!armed || busy}
              className="border border-[#8B1C22] text-[#8B1C22] hover:bg-[#8B1C22] hover:text-white inline-flex items-center gap-1 text-xs px-3 py-1.5 uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="reset-demo-run-btn"
            >
              <Trash2 className="w-3 h-3" />
              {busy ? "…" : (lang === "es" ? "Restablecer" : "Reset")}
            </button>
            <button
              onClick={loadPreview}
              disabled={busy}
              className="text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 text-xs px-2 py-1"
              title={lang === "es" ? "Actualizar vista previa" : "Refresh preview"}
              data-testid="reset-demo-refresh-btn"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetDemoDataCard;
